/**
 * ThemeToggle — three-way cycler for the visitor's theme preference
 * (System ⇄ Light ⇄ Dark). The currently-active mode is highlighted;
 * clicking the button itself cycles to the next mode in the order
 * System → Light → Dark → System.
 *
 * Rendered as a compact icon strip ("☼ ☽ ⚙") in PilotNavbar's
 * actions slot. Each icon is independently clickable so the visitor
 * can jump straight to a mode instead of cycling.
 *
 * State lives in `useThemePreference`; this component is a thin view
 * + click bridge over the hook.
 */

import React from 'react';
import {clsx} from 'clsx/lite';
import {useThemePreference, type ThemeMode} from '../../hooks/useThemePreference';

const MODES: {
  id: ThemeMode;
  label: string;
  icon: React.ReactNode;
}[] = [
  {
    id: 'light',
    label: 'Light theme',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="size-4"
      >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    ),
  },
  {
    id: 'dark',
    label: 'Dark theme',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="size-4"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    ),
  },
  {
    id: 'system',
    label: 'Match system',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="size-4"
      >
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <path d="M8 20h8M12 16v4" />
      </svg>
    ),
  },
];

export function ThemeToggle({className}: {className?: string} = {}) {
  const {mode, setMode} = useThemePreference();

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className={clsx(
        'theme-toggle inline-flex items-center gap-0.5 rounded-full border border-taupe-300 bg-white p-0.5 shadow-sm dark:border-taupe-700 dark:bg-taupe-900',
        className,
      )}
    >
      {MODES.map((m) => {
        const isActive = mode === m.id;
        return (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={m.label}
            title={m.label}
            onClick={() => setMode(m.id)}
            className={clsx(
              'theme-toggle-btn inline-flex size-7 items-center justify-center rounded-full transition-colors',
              isActive
                ? 'bg-taupe-950 text-white dark:bg-taupe-100 dark:text-taupe-950'
                : 'text-taupe-600 hover:text-taupe-950 dark:text-taupe-400 dark:hover:text-white',
            )}
          >
            {m.icon}
          </button>
        );
      })}
    </div>
  );
}

export default ThemeToggle;
